
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface TimeSlotListProps {
  selectedTimeSlot: string | null;
  onTimeSlotClick: (slotId: string) => void;
}

export const TimeSlotList = ({ selectedTimeSlot, onTimeSlotClick }: TimeSlotListProps) => {
  const { data: timeSlots = [] } = useQuery({
    queryKey: ['timeSlotsToSelect'],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const now = new Date();

      // First, get user's existing bookings to check if they've already booked
      const { data: userBookings } = await supabase
        .from('bookings')
        .select('time_slot_id')
        .eq('user_id', session?.session?.user?.id || '');

      // Get all available time slots
      const { data, error } = await supabase
        .from('time_slots_to_select')
        .select('*')
        .gte('date', now.toISOString().split('T')[0])
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      
      if (error) {
        console.error('Error fetching time slots:', error);
        return [];
      }

      // Filter out slots that the user has already booked
      const userBookedSlotIds = new Set(userBookings?.map(booking => booking.time_slot_id) || []);

      return data
        .filter(slot => {
          const hasAvailableSpots = slot.booked_players < slot.max_players;
          const hasNotBooked = !userBookedSlotIds.has(slot.created_time_slot_id);
          return hasAvailableSpots && hasNotBooked;
        })
        .map(slot => ({
          ...slot,
          date: new Date(slot.date),
          created_at: new Date(slot.created_at)
        }));
    },
  });

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const period = Number(hours) >= 12 ? 'PM' : 'AM';
    const hour12 = Number(hours) % 12 || 12;
    return `${hour12}:${minutes} ${period}`;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h2 className="text-xl font-semibold mb-6">Select Time Slot</h2>
      <div className="space-y-4">
        {timeSlots.map((slot) => {
          const totalBooked = slot.booked_players + slot.reserved_slots;
          const availableSpots = slot.max_players - totalBooked;
          
          return (
            <div
              key={slot.id}
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                selectedTimeSlot === slot.id
                  ? "bg-primary/10 border-2 border-primary"
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
              onClick={() => onTimeSlotClick(slot.id)}
            >
              <h3 className="font-medium">
                {format(new Date(slot.date), "EEEE, MMMM d, yyyy")}
                {" at "}
                {formatTime(slot.start_time)}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Maximum {slot.max_guests_per_player} guests per player • {slot.fee} AED
              </p>
              <div className="flex gap-2 mt-1 text-sm">
                <span className="text-orange-600 font-medium">
                  {totalBooked} spots booked
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-emerald-600 font-medium">
                  {availableSpots} spots available
                </span>
              </div>
            </div>
          );
        })}
        
        {timeSlots.length === 0 && (
          <p className="text-gray-500 text-center py-4">
            No time slots available at the moment
          </p>
        )}
      </div>
    </div>
  );
};
